import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useCart } from "../context/CartContext";
import { imgUrl, productAlt } from "../lib/api";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { addToCart, wishlist, toggleWishlist, setCartOpen } = useCart();
  const wished = wishlist.some((p) => p.id === product.id);
  const price = product.sale_price || product.price;

  return (
    <div
      className="group animate-fade-in-up"
      style={{ animationDelay: `${(index % 8) * 60}ms` }}
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative overflow-hidden bg-white border border-black/10 transition-colors duration-500 group-hover:border-gold/60">
        <Link to={`/product/${product.slug}`} data-testid={`product-link-${product.slug}`}>
          <img
            src={imgUrl(product.images?.[0])}
            alt={productAlt(product)}
            loading="lazy"
            className="w-full aspect-square object-cover transition-transform duration-700 ease-out group-hover:scale-[1.07]"
          />
          <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(420px circle at 50% 38%, rgba(212,175,55,0.20), transparent 65%)" }} />
        </Link>
        {product.sale_price && (
          <span className="absolute top-3 left-3 bg-gold text-[#111] text-[10px] uppercase tracking-widest px-2 py-1" data-testid={`sale-badge-${product.slug}`}>Sale</span>
        )}
        {product.bestseller && (
          <span className="absolute top-3 left-3 bg-[#111] text-[#FAF9F6] text-[10px] uppercase tracking-widest px-2 py-1" style={{ marginLeft: product.sale_price ? "52px" : "0" }} data-testid={`bestseller-badge-${product.slug}`}>Bestseller</span>
        )}
        {product.new_arrival && (
          <span className="absolute top-3 left-3 border border-gold text-gold bg-white/80 text-[10px] uppercase tracking-widest px-2 py-1" style={{ marginLeft: product.sale_price || product.bestseller ? "104px" : "0" }} data-testid={`new-badge-${product.slug}`}>New</span>
        )}
        {product.stock <= 0 && (
          <span className="absolute top-3 right-3 bg-[#111] text-[#FAF9F6] text-[10px] uppercase tracking-widest px-2 py-1">Sold Out</span>
        )}
        <button
          onClick={() => { toggleWishlist(product); toast(wished ? "Removed from wishlist" : "Added to wishlist"); }}
          className="absolute bottom-3 right-3 bg-white/80 backdrop-blur p-2 border border-black/10 hover:border-gold transition-colors duration-300"
          aria-label="Toggle wishlist"
          data-testid={`wishlist-toggle-${product.slug}`}
        >
          <Heart strokeWidth={1.5} size={16} className={wished ? "fill-gold text-gold" : ""} />
        </button>
      </div>
      <div className="pt-4">
        <p className="small-caps text-muted-foreground">{product.category_name}</p>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-serif text-lg mt-1 leading-snug hover:text-gold transition-colors duration-300">{product.name}</h3>
        </Link>
        <div className="flex items-center justify-between mt-2">
          <p className="text-sm" data-testid={`price-${product.slug}`}>
            <span className="font-semibold">₹{price.toLocaleString("en-IN")}</span>
            {product.sale_price && <span className="text-muted-foreground line-through ml-2 text-xs">₹{product.price.toLocaleString("en-IN")}</span>}
          </p>
          <button
            onClick={() => { addToCart(product, 1); toast.success("Added to cart"); setCartOpen(true); }}
            disabled={product.stock <= 0}
            className="small-caps border-b border-black hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-40"
            data-testid={`add-to-cart-${product.slug}`}
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}
